/**
 * Insights de pico — aba Carga (faixa compacta horizontal).
 */
import React, { useMemo } from 'react';
import type { HeatmapMatrix } from '../../../services/heatmapService';
import { DAY_LABELS } from '../../../services/heatmapService';

interface OpsLoadPeakBarProps {
  matrix: HeatmapMatrix | undefined;
  capacityPerHour: number | null;
}

export const OpsLoadPeakBar: React.FC<OpsLoadPeakBarProps> = ({ matrix, capacityPerHour }) => {
  const stats = useMemo(() => {
    const cells = matrix?.cells ?? [];
    if (cells.length === 0 || capacityPerHour == null || capacityPerHour <= 0) {
      return { known: false, weeklyAvg: 0, weeklyPeak: 0, monthlyPeak: 0, criticalHour: null as number | null, criticalDay: null as string | null };
    }

    const occupancies = cells.map((c) => ({
      pct: (c.count / capacityPerHour) * 100,
      hour: c.hour,
      day: c.day,
    }));

    const counts = occupancies.map((o) => o.pct);
    const weeklyAvg = counts.reduce((s, v) => s + v, 0) / counts.length;
    const weeklyPeak = Math.max(...counts);
    const monthlyPeak = weeklyPeak;

    const peakCell = occupancies.reduce((best, cur) => (cur.pct > best.pct ? cur : best), occupancies[0]);
    const criticalHour = peakCell && peakCell.pct > 80 ? peakCell.hour : null;
    const criticalDay = peakCell && peakCell.pct > 80 ? DAY_LABELS[peakCell.day as keyof typeof DAY_LABELS] ?? null : null;

    return { known: true, weeklyAvg, weeklyPeak, monthlyPeak, criticalHour, criticalDay };
  }, [matrix, capacityPerHour]);

  const percent = (value: number) => (stats.known ? `${Math.round(value)}%` : '—');
  const items = [
    { label: 'Média sem.', value: percent(stats.weeklyAvg) },
    { label: 'Pico sem.', value: percent(stats.weeklyPeak), warn: stats.known && stats.weeklyPeak > 100 },
    { label: 'Pico mens.', value: percent(stats.monthlyPeak), warn: stats.known && stats.monthlyPeak > 100 },
    { label: 'Hora crít.', value: stats.criticalHour != null ? `${String(stats.criticalHour).padStart(2, '0')}h` : '—', warn: stats.criticalHour != null },
    { label: 'Dia crít.', value: stats.criticalDay ?? '—', warn: !!stats.criticalDay },
  ];

  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-[var(--border-default)]/60 bg-[var(--bg-surface)] px-3 py-1">
      <span className="shrink-0 text-[8px] font-bold uppercase tracking-[0.14em] text-[var(--text-muted)]">
        Antecipação de Gargalos
      </span>
      <div className="flex min-w-0 flex-1 items-stretch divide-x divide-[var(--border-default)]/50">
        {items.map((item) => (
          <div key={item.label} className="flex min-w-0 flex-1 items-baseline gap-1.5 px-2.5 py-0.5">
            <span className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
              {item.label}
            </span>
            <span className={`truncate text-[11px] font-bold tabular-nums ${item.warn ? 'text-red-500' : 'text-[var(--text-primary)]'}`}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OpsLoadPeakBar;
